/**
 * /agents/findings — the phishing/spam report. Filterable table of flagged
 * emails; click a row for a detail drawer (headers, indicators, reasons) with
 * action buttons: mark reviewed / false-positive, and label / quarantine /
 * trash the message in the user's Gmail (via DWD).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Text,
  Button,
  Select,
  Input,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Divider,
  Wrap,
  WrapItem,
  Tag,
  useDisclosure,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import { callAgents } from '../../hooks/useAgents';
import AgentStatusChip from '../../components/Agents/AgentStatusChip';

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

export default function PhishingFindingsPage() {
  const location = useLocation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [findings, setFindings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', kind: '', severity: '', mailbox: '' });
  const [selected, setSelected] = useState(null);
  const [acting, setActing] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.400');
  const codeBg = useColorModeValue('gray.50', 'gray.900');

  // Honor ?agentId= coming from the agent detail page.
  const agentId = new URLSearchParams(location.search).get('agentId') || null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callAgents('listFindings', {
        agentId,
        status: filters.status || undefined,
        kind: filters.kind || undefined,
        severity: filters.severity || undefined,
        mailbox: filters.mailbox || undefined,
        limit: 200,
      });
      setFindings(res.findings || []);
      setTotal(res.total || 0);
    } catch (err) {
      toast({ title: 'Load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [agentId, filters, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openFinding = (f) => {
    setSelected(f);
    onOpen();
  };

  const updateStatus = async (status) => {
    if (!selected) return;
    setActing(true);
    try {
      const res = await callAgents('updateFinding', { id: selected.id, status });
      setSelected(res.finding);
      toast({ title: `Marked ${status.replace('_', ' ')}`, status: 'success', duration: 2000 });
      await load();
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setActing(false);
    }
  };

  const act = async (action) => {
    if (!selected) return;
    const verb = action === 'trash' ? 'Trash' : action === 'quarantine' ? 'Quarantine' : 'Label';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`${verb} this message in ${selected.mailbox_email}?`)) return;
    setActing(true);
    try {
      const res = await callAgents('actOnFinding', { id: selected.id, act: action });
      setSelected(res.finding);
      toast({ title: `${verb} done`, status: 'success', duration: 2500 });
      await load();
    } catch (err) {
      toast({ title: `${verb} failed`, description: err.message, status: 'error', duration: 7000 });
    } finally {
      setActing(false);
    }
  };

  const ind = selected?.indicators || {};

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={4} flexWrap="wrap" spacing={3}>
        <Box>
          <Heading size="md">Findings</Heading>
          <Text fontSize="xs" color={subtle}>{total} flagged email{total === 1 ? '' : 's'}{agentId ? ' (filtered to one agent)' : ''}</Text>
        </Box>
        <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={load} isLoading={loading}>Refresh</Button>
      </HStack>

      <HStack mb={4} spacing={3} flexWrap="wrap">
        <Select size="sm" maxW="160px" placeholder="All statuses" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
          <option value="new">New</option>
          <option value="reviewed">Reviewed</option>
          <option value="confirmed">Confirmed</option>
          <option value="false_positive">False positive</option>
        </Select>
        <Select size="sm" maxW="150px" placeholder="All kinds" value={filters.kind} onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))}>
          <option value="phishing">Phishing</option>
          <option value="spam">Spam</option>
          <option value="suspicious">Suspicious</option>
        </Select>
        <Select size="sm" maxW="150px" placeholder="All severities" value={filters.severity} onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))}>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </Select>
        <Input size="sm" maxW="220px" placeholder="Filter by mailbox" value={filters.mailbox} onChange={(e) => setFilters((f) => ({ ...f, mailbox: e.target.value }))} />
      </HStack>

      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden">
        {loading && !findings.length ? (
          <Center py={16}><Spinner color="blue.400" /></Center>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr><Th>Severity</Th><Th>Kind</Th><Th>Mailbox</Th><Th>From</Th><Th>Subject</Th><Th>Conf.</Th><Th>Action</Th><Th>Status</Th><Th>When</Th></Tr>
            </Thead>
            <Tbody>
              {findings.map((f) => (
                <Tr key={f.id} _hover={{ bg: cardBorder, cursor: 'pointer' }} onClick={() => openFinding(f)}>
                  <Td><AgentStatusChip value={f.severity} /></Td>
                  <Td><AgentStatusChip value={f.kind} /></Td>
                  <Td fontSize="xs">{f.mailbox_email}</Td>
                  <Td fontSize="xs" maxW="180px" isTruncated>{f.from_email}</Td>
                  <Td fontSize="xs" maxW="240px" isTruncated>{f.subject}</Td>
                  <Td fontSize="xs">{f.confidence != null ? `${Math.round(f.confidence * 100)}%` : '—'}</Td>
                  <Td><AgentStatusChip value={f.action_taken} /></Td>
                  <Td><AgentStatusChip value={f.status} /></Td>
                  <Td fontSize="xs">{fmtDate(f.created_at)}</Td>
                </Tr>
              ))}
              {!findings.length && (
                <Tr><Td colSpan={9}><Center py={12}><Text fontSize="sm" color={subtle}>No findings{Object.values(filters).some(Boolean) ? ' for these filters' : ' yet'}.</Text></Center></Td></Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <HStack spacing={2} flexWrap="wrap">
              {selected && <AgentStatusChip value={selected.kind} />}
              {selected && <AgentStatusChip value={selected.severity} />}
              <Text fontSize="sm">{selected ? `${Math.round((selected.confidence || 0) * 100)}% confidence` : ''}</Text>
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            {selected && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontSize="xs" color={subtle}>Subject</Text>
                  <Text fontWeight="medium">{selected.subject}</Text>
                </Box>
                <HStack justify="space-between" align="start">
                  <Box>
                    <Text fontSize="xs" color={subtle}>From</Text>
                    <Text fontSize="sm">{selected.from_name}</Text>
                    <Text fontSize="sm" color={subtle}>{selected.from_email}</Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="xs" color={subtle}>Mailbox</Text>
                    <Text fontSize="sm">{selected.mailbox_email}</Text>
                    <Text fontSize="xs" color={subtle}>{fmtDate(selected.received_at)}</Text>
                  </Box>
                </HStack>

                <Box>
                  <Text fontSize="xs" color={subtle} mb={1}>Snippet</Text>
                  <Box bg={codeBg} borderRadius="md" p={3} fontSize="sm">{selected.snippet || '(none)'}</Box>
                </Box>

                <Box>
                  <Text fontSize="xs" color={subtle} mb={1}>Why it was flagged</Text>
                  <VStack align="stretch" spacing={1}>
                    {(selected.reasons || []).map((r, i) => (
                      <Text key={i} fontSize="sm">• {r}</Text>
                    ))}
                    {!(selected.reasons || []).length && <Text fontSize="sm" color={subtle}>No model reasons recorded.</Text>}
                  </VStack>
                </Box>

                <Box>
                  <Text fontSize="xs" color={subtle} mb={1}>Signals</Text>
                  <Wrap>
                    <WrapItem><Tag size="sm" colorScheme={ind.spf === 'pass' ? 'green' : 'red'}>SPF: {ind.spf || 'n/a'}</Tag></WrapItem>
                    <WrapItem><Tag size="sm" colorScheme={ind.dkim === 'pass' || ind.dkim === 'present' ? 'green' : 'red'}>DKIM: {ind.dkim || 'n/a'}</Tag></WrapItem>
                    <WrapItem><Tag size="sm" colorScheme={ind.dmarc === 'pass' ? 'green' : 'red'}>DMARC: {ind.dmarc || 'n/a'}</Tag></WrapItem>
                    {ind.replyToMismatch && <WrapItem><Tag size="sm" colorScheme="orange">Reply-To mismatch</Tag></WrapItem>}
                    {ind.displayNameSpoof && <WrapItem><Tag size="sm" colorScheme="red">Display-name spoof</Tag></WrapItem>}
                    {ind.lookalikeDomain && <WrapItem><Tag size="sm" colorScheme="red">Look-alike domain</Tag></WrapItem>}
                    {ind.ipUrl && <WrapItem><Tag size="sm" colorScheme="red">Raw IP link</Tag></WrapItem>}
                    {ind.shortenerUrl && <WrapItem><Tag size="sm" colorScheme="yellow">URL shortener</Tag></WrapItem>}
                    {ind.riskyAttachment && <WrapItem><Tag size="sm" colorScheme="red">Risky attachment</Tag></WrapItem>}
                    {ind.urgentLanguage && <WrapItem><Tag size="sm" colorScheme="yellow">Urgency</Tag></WrapItem>}
                    {ind.credentialRequest && <WrapItem><Tag size="sm" colorScheme="orange">Credential/payment ask</Tag></WrapItem>}
                    {ind.brandImpersonated && <WrapItem><Tag size="sm" colorScheme="purple">Impersonates {ind.brandImpersonated}</Tag></WrapItem>}
                  </Wrap>
                </Box>

                {ind.verifier && (
                  <Box>
                    <Text fontSize="xs" color={subtle} mb={1}>Verifier (second opinion before acting)</Text>
                    <Wrap mb={1}>
                      <WrapItem>
                        <Tag
                          size="sm"
                          colorScheme={{ confirmed: 'green', downgraded: 'orange', overruled: 'red', unavailable: 'gray' }[ind.verifier.outcome] || 'gray'}
                        >
                          {ind.verifier.outcome}
                        </Tag>
                      </WrapItem>
                      {ind.verifier.model && <WrapItem><Tag size="sm" variant="outline">{ind.verifier.model}</Tag></WrapItem>}
                      {ind.verifier.classification && (
                        <WrapItem>
                          <Tag size="sm" variant="subtle">
                            says {ind.verifier.classification} ({Math.round((ind.verifier.confidence || 0) * 100)}%)
                          </Tag>
                        </WrapItem>
                      )}
                    </Wrap>
                    {(ind.verifier.reasons || []).map((r, i) => (
                      <Text key={i} fontSize="xs" color={subtle}>• {r}</Text>
                    ))}
                  </Box>
                )}

                {(ind.externalLinkDomains || []).length > 0 && (
                  <Box>
                    <Text fontSize="xs" color={subtle} mb={1}>External link domains</Text>
                    <Wrap>
                      {ind.externalLinkDomains.map((d) => <WrapItem key={d}><Tag size="sm" variant="outline">{d}</Tag></WrapItem>)}
                    </Wrap>
                  </Box>
                )}

                <Divider />

                <Box>
                  <Text fontSize="xs" color={subtle} mb={2}>Current action: <AgentStatusChip value={selected.action_taken} /> · status: <AgentStatusChip value={selected.status} /></Text>
                  <Text fontSize="xs" color={subtle} mb={2}>Review</Text>
                  <HStack mb={3}>
                    <Button size="sm" variant="outline" onClick={() => updateStatus('reviewed')} isLoading={acting}>Reviewed</Button>
                    <Button size="sm" variant="outline" colorScheme="gray" onClick={() => updateStatus('false_positive')} isLoading={acting}>False positive</Button>
                    <Button size="sm" variant="outline" colorScheme="red" onClick={() => updateStatus('confirmed')} isLoading={acting}>Confirm threat</Button>
                  </HStack>
                  <Text fontSize="xs" color={subtle} mb={2}>Act on the message in Gmail</Text>
                  <HStack>
                    <Button size="sm" colorScheme="blue" variant="outline" onClick={() => act('label')} isLoading={acting} isDisabled={!selected.gmail_message_id}>Label</Button>
                    <Button size="sm" colorScheme="purple" variant="outline" onClick={() => act('quarantine')} isLoading={acting} isDisabled={!selected.gmail_message_id}>Quarantine</Button>
                    <Button size="sm" colorScheme="red" onClick={() => act('trash')} isLoading={acting} isDisabled={!selected.gmail_message_id}>Trash</Button>
                  </HStack>
                </Box>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}
